import i18n from '@/locale';

/**
 * Quiz HTML Generator
 * 生成包含題號的題目 HTML，支援 MathJax 數學公式
 */
export class QuizHtmlGenerator {
  /**
   * 生成單個題目的 HTML
   * @param {Object} question - 題目對象
   * @param {Number} index - 題號（從 1 開始）
   * @returns {String} HTML 字符串
   */
  generateQuestionHtml(question, index) {
    const { type, question: content, options, point } = question;

    let html = `
      <div class="quiz-container" style="
        padding: 40px;
        font-family: 'Microsoft YaHei', 'PingFang TC', 'Noto Sans TC', sans-serif;
        background: white;
        max-width: 800px;
        line-height: 1.8;
      ">
        <!-- 題號 + 題幹 -->
        <div class="quiz-question" style="
          font-size: 18px;
          line-height: 2;
          margin-bottom: 20px;
          color: #333;
        ">
          <span class="quiz-number" style="
            font-weight: bold;
            margin-right: 10px;
            color: #2d8cf0;
            font-size: 20px;
          ">${index}.</span>
          <span class="quiz-content">${this.escapeHtml(content)}</span>
          ${point ? `<span style="
            color: #999;
            font-size: 14px;
            margin-left: 10px;
          ">${i18n.t('board.xlsxImport.分數格式', { point })}</span>` : ''}
        </div>
    `;

    // 單選題或判斷題：顯示選項
    if ((type === 'single' || type === 'judge') && options && options.length > 0) {
      html += '<div class="quiz-options" style="padding-left: 40px;">';
      options.forEach((option, i) => {
        const letter = String.fromCharCode(65 + i); // A, B, C, D
        html += `
          <div class="quiz-option" style="
            margin: 12px 0;
            font-size: 16px;
            color: #555;
          ">
            <span style="
              font-weight: 600;
              color: #2d8cf0;
              margin-right: 8px;
            ">(${letter})</span>
            <span>${this.escapeHtml(option)}</span>
          </div>
        `;
      });
      html += '</div>';
    }

    // 填空題或主觀題：顯示答題區
    if (type === 'complete' || type === 'subjective') {
      html += `
        <div class="quiz-answer-area" style="
          margin-top: 20px;
          padding-left: 40px;
        ">
          <div style="
            border-bottom: 2px solid #ddd;
            padding: 15px 0;
            min-height: 50px;
          ">
            <span style="
              color: #999;
              font-size: 15px;
            ">答：_______________________________________________________</span>
          </div>
        </div>
      `;
    }

    html += '</div>';
    return html;
  }

  /**
   * XSS 防護：轉義 HTML 特殊字符（但保留 MathJax 公式）
   * @param {String} text - 原始文本
   * @returns {String} 轉義後的文本
   */
  escapeHtml(text) {
    if (!text) return '';

    // 保護 MathJax 公式不被轉義
    const formulas = [];
    let processedText = text;

    // ⚠️ 重要：必須先處理 $$...$$ 再處理 $...$，避免誤判
    // 提取並保護獨立公式 $$...$$
    processedText = processedText.replace(/\$\$([^\$]+)\$\$/g, (match, formula) => {
      const placeholder = `__DISPLAY_FORMULA_${formulas.length}__`;
      formulas.push(match);
      return placeholder;
    });

    // 提取並保護行內公式 $...$
    processedText = processedText.replace(/\$([^\$]+)\$/g, (match, formula) => {
      const placeholder = `__FORMULA_${formulas.length}__`;
      formulas.push(match);
      return placeholder;
    });

    // 轉義普通 HTML 字符
    const div = document.createElement('div');
    div.textContent = processedText;
    let escaped = div.innerHTML;

    // 還原 MathJax 公式
    formulas.forEach((formula, i) => {
      const placeholder = formula.startsWith('$$')
        ? `__DISPLAY_FORMULA_${i}__`
        : `__FORMULA_${i}__`;
      escaped = escaped.replace(placeholder, formula);
    });

    return escaped;
  }

  /**
   * 生成多個題目的 HTML（用於批次處理）
   * @param {Array} questions - 題目數組
   * @returns {Array} HTML 字符串數組
   */
  generateBatch(questions) {
    return questions.map((question, index) =>
      this.generateQuestionHtml(question, index + 1)
    );
  }
}
